import React, { useState, useEffect } from 'react';
import { Ban, ExternalLink } from 'lucide-react';
import { WindowFrame } from './WindowFrame';
import { BaseNodeWrapper } from './BaseNodeWrapper';
import { NodeFrameWrapper } from './NodeFrameWrapper';

interface IframeNodeProps {
  id: string;
  src: string;
  title?: string;
  frameStyle: 'browser' | 'none';
  isEditing?: boolean;
  borderRadius?: number;
  workInProgress?: boolean;
  wipBannerOffset?: number;
  iframeZoom?: number;
  onTitleChange?: (title: string) => void;
  onUpdate?: (updates: { wipBannerOffset?: number; borderRadius?: number; iframeZoom?: number }) => void;
}

export const IframeNode: React.FC<IframeNodeProps> = ({
  src,
  title,
  frameStyle,
  isEditing,
  borderRadius = 8,
  workInProgress = false,
  wipBannerOffset = 0,
  iframeZoom = 1,
  onTitleChange,
  onUpdate,
}) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);

    // Listen for CSP/X-Frame-Options errors via console
    const handleError = (event: ErrorEvent) => {
      if (event.message && event.message.includes('frame')) {
        setHasError(true);
      }
    };

    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('error', handleError);
    };
  }, [src]);

  const iframeContent = (
    <div className="w-full h-full relative overflow-hidden">
      {hasError ? (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-800 text-gray-300 p-8">
          <div className="text-center space-y-4">
            <Ban size={48} className="text-gray-500" />
            <div className="space-y-2">
              <p className="font-medium">This website cannot be embedded</p>
              <p className="text-sm text-gray-400">
                The website's security policy prevents it from being displayed in an iframe.
              </p>
            </div>
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
            >
              <span>Open in new tab</span>
              <ExternalLink size={20} />
            </a>
          </div>
        </div>
      ) : (
        <iframe
          src={src}
          className="iframe-zoomable"
          style={{
            width: `${100 / iframeZoom}%`,
            height: `${100 / iframeZoom}%`,
            pointerEvents: isEditing ? 'none' : 'auto',
            transform: `scale(${iframeZoom})`,
            WebkitTransform: `scale(${iframeZoom})`,
            MozTransform: `scale(${iframeZoom})`,
          }}
          title={title || 'Embedded content'}
          onError={() => setHasError(true)}
        />
      )}
    </div>
  );

  if (frameStyle === 'none') {
    return (
      <BaseNodeWrapper
        workInProgress={workInProgress}
        wipBannerOffset={wipBannerOffset}
        isEditing={isEditing}
        onUpdate={onUpdate}
      >
        <NodeFrameWrapper borderRadius={borderRadius}>
          {iframeContent}
        </NodeFrameWrapper>
      </BaseNodeWrapper>
    );
  }

  return (
    <BaseNodeWrapper
      workInProgress={workInProgress}
      wipBannerOffset={wipBannerOffset}
      isEditing={isEditing}
      onUpdate={onUpdate}
    >
      <WindowFrame
        title={title}
        onTitleChange={onTitleChange}
        isEditing={isEditing}
        borderRadius={borderRadius}
      >
        <div className="w-full h-full overflow-hidden">
          {iframeContent}
        </div>
      </WindowFrame>
    </BaseNodeWrapper>
  );
};
